import React, { useCallback, useRef, useState } from 'react';

import { BuilderBlocks } from '@builder.io/react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Tabs from '~/acquisition/components/Tabs';
import { useScrollVisibility } from '~/acquisition/utils/useNavOffsetHeight';
import useBuilderPostRegTabId from '~/acquisition/utils/usePostRegTabId';
import { useSelectActiveTab } from '~/acquisition/utils/useSelectActiveTab';
import PostregCountdown from '~/shared/components/PostregCountdown/PostregCountdown';
import { useNavElements } from '~/shared/utils/NavElements';
import scrollToRef from '~/shared/utils/scrollToRef';
import {
  Carousel,
  useBreakpoint,
  desktop,
  HeaderAttachment,
} from '~/techstyle-shared/react-components';
import { buildURL } from '~/techstyle-shared/redux-core';

const PostRegHeader = styled.header`
  z-index: 3;
  isolation: isolate;
  width: 100%;
`;

const TabLabel = styled.button.attrs({ variant: 'unstyled' })`
  background: transparent;
  padding: 14px 8px;
  border: none;
  height: 100%;
  min-height: 200px;
`;

const CarouselWrapper = styled.div`
  margin-bottom: 12px;
  ${({ theme: { tabsNode } }) => tabsNode.carousel};
`;

const TabWrapper = styled.div`
  width: 100%;
  justify-content: center;
  // Remove margin-right:auto via unset
  margin-right: unset;
`;

const cellStyle = css`
  width: 152px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PostregWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  font-size: 20px;
  font-weight: 600;

  ${({ theme }) => theme.tabsNode.countdownWrapper};

  ${desktop`
    margin-top: 16px;
  `}
`;

const TextTabs = styled(Tabs)`
  z-index: 1;
`;

export const Context = React.createContext();

const BuilderTabs = ({
  builderBlock,
  tabs,
  hideScrollingLabels,
  includePostregCountdownTimer,
  postregTimerText,
}) => {
  const { headerWrapperRef } = useNavElements();
  const { isMobile } = useBreakpoint();
  const bannerRef = useRef();
  const carouselRef = useRef();
  const router = useRouter();
  const postRegHeaderRef = useRef();
  const isScrollingTabsVisible = useScrollVisibility(postRegHeaderRef);
  const { tabIndex } = useBuilderPostRegTabId(tabs);

  // Always accepts a tab index # (not a string)
  const [activeTab, setActiveTab] = useState(tabIndex);
  const { flickityRefSetter } = useSelectActiveTab(activeTab);

  const PostRegBannerComponent = styled(HeaderAttachment)``;

  const handleTabSelect = useCallback(
    (tabIndex, scroll = true) => {
      const tab = tabs[tabIndex];

      if (tab?.tabId) {
        const pathname = router.pathname;
        const query = {
          ...router.query,
          tab: tab.tabId,
        };
        const asPath = buildURL(window.location.pathname, query);

        router.replace(
          {
            pathname,
            query,
          },
          asPath,
          {
            shallow: true,
          }
        );
      }

      setActiveTab(tabIndex);

      if (scroll) {
        setTimeout(
          () =>
            scrollToRef(headerWrapperRef, {
              block: 'start',
              behavior: 'smooth',
            }),
          20
        );
      }
    },
    [headerWrapperRef, router, tabs]
  );

  if (!tabs?.length) {
    return null;
  }

  return (
    <TabWrapper data-builder-component-type="tabs">
      <Context.Provider value={{ isBuilderTabs: true }}>
        <PostRegHeader ref={postRegHeaderRef}>
          <CarouselWrapper
            className="builder-tab-carousel-wrapper"
            ref={carouselRef}
          >
            <Carousel
              flickityRef={flickityRefSetter}
              pageDots={false}
              contain
              imagesLoaded
              resize
              adaptiveHeight
              prevNextButtons={false}
              autoPlay={false}
              wrapAround={false}
              cellStyle={cellStyle}
              draggable={isMobile}
              freeScroll={isMobile}
              selectedAttraction={isMobile ? 0.025 : 0.01}
              friction={isMobile ? 0.28 : 0.21}
            >
              {tabs?.map((tab, index) => (
                <TabLabel
                  key={`${tab.tabId}-${index}`}
                  onClick={() => {
                    handleTabSelect(index, false);
                  }}
                  className="builder-tab-tab-label"
                >
                  <BuilderBlocks
                    parentElementId={builderBlock.id}
                    dataPath={`component.options.tabs.${index}.label`}
                    blocks={tab.label}
                    className={
                      activeTab === index
                        ? 'builder-tab-builder-block-tab-tile selected'
                        : 'builder-tab-builder-block-tab-tile'
                    }
                  />
                </TabLabel>
              ))}
            </Carousel>
          </CarouselWrapper>

          {/* Include the postreg timer in the builder tabs. Note: The scrolling label is baked into the TextTabs component that renders the tabs label */}
          {includePostregCountdownTimer && (
            <PostregWrapper>
              <PostregCountdown timerText={postregTimerText} />
            </PostregWrapper>
          )}

          {!hideScrollingLabels && isScrollingTabsVisible && (
            <PostRegBannerComponent>
              <TextTabs
                bannerRef={bannerRef}
                isInHeader={isScrollingTabsVisible}
                handleTabSelect={handleTabSelect}
                selectedTabId={activeTab}
                options={{
                  hideCountdownTimer: !includePostregCountdownTimer,
                }}
                tabs={tabs?.map((tab, index) => ({
                  id: index,
                  text:
                    tab.scrolledLabel || `Set Scrolling Text for Tab ${index}`,
                }))}
              />
            </PostRegBannerComponent>
          )}
        </PostRegHeader>

        {tabs?.[activeTab] && (
          <BuilderBlocks
            parentElementId={builderBlock.id}
            dataPath={`component.options.tabs.${activeTab}.content`}
            blocks={tabs[activeTab].content}
          />
        )}
      </Context.Provider>
    </TabWrapper>
  );
};

BuilderTabs.propTypes = {
  /**
   * The builderBlock object this is rendering.
   */
  builderBlock: PropTypes.shape({
    id: PropTypes.string,
  }),

  /**
   * Boolean to determine if the component should hide the scrolling tab labels. Defaults to false (show the labels).
   */
  hideScrollingLabels: PropTypes.bool,

  /**
   * Boolean to determine if component should include a postreg timer.
   */
  includePostregCountdownTimer: PropTypes.bool,

  /**
   * Text string for postreg timer.
   */
  postregTimerText: PropTypes.string,

  /**
   * The builder tab objects to display. These send in a label uiBlocks object and a content uiBlocks object per tab.
   */
  tabs: PropTypes.array,
};

export default BuilderTabs;
